export interface LiveComposerEvent {
  id: string;
  midiNotes: number[];
  capturedAtMs: number;
  source: 'midi' | 'audio';
  restBeforeDivisions: number;
  durationDivisions: number;
  endedAtMs?: number;
}

export interface LiveComposerPedalEvent {
  id: string;
  capturedAtMs: number;
  source: 'midi';
  pedalDown: boolean;
  positionDivisions: number;
}

export interface LiveComposerScoreBuild {
  xml: string;
  measureCount: number;
}

type StaffNumber = 1 | 2;
type DurationType = '16th' | 'eighth' | 'quarter' | 'half' | 'whole';
type OrnamentType = 'trill';
type NotatedComposerEvent = Pick<
  LiveComposerEvent,
  'midiNotes' | 'restBeforeDivisions' | 'durationDivisions'
> & {
  ornament?: OrnamentType;
};

const DIVISIONS = 4;
const BEATS_PER_MEASURE = 4;
const MEASURE_DURATION = DIVISIONS * BEATS_PER_MEASURE;
const MIN_MEASURES = 4;
const EXTRA_DIVISIONS_AHEAD = MEASURE_DURATION;
const STAFF_SPLIT_MIDI = 60;
const TRILL_MIN_NOTES = 4;
const TRILL_MAX_INTERVAL_SEMITONES = 2;
const TRILL_REQUIRED_DIVISIONS = 1;

const DURATION_PARTS: Array<{ duration: number; type: DurationType }> = [
  { duration: 16, type: 'whole' },
  { duration: 8, type: 'half' },
  { duration: 4, type: 'quarter' },
  { duration: 2, type: 'eighth' },
  { duration: 1, type: '16th' },
];

const pitchClassMap = [
  { step: 'C', alter: null as number | null },
  { step: 'C', alter: 1 },
  { step: 'D', alter: null },
  { step: 'D', alter: 1 },
  { step: 'E', alter: null },
  { step: 'F', alter: null },
  { step: 'F', alter: 1 },
  { step: 'G', alter: null },
  { step: 'G', alter: 1 },
  { step: 'A', alter: null },
  { step: 'A', alter: 1 },
  { step: 'B', alter: null },
];

const midiToPitch = (midi: number) => {
  const normalizedMidi = Math.max(0, Math.min(127, Math.round(midi)));
  const pitchClass = pitchClassMap[((normalizedMidi % 12) + 12) % 12];
  const octave = Math.floor(normalizedMidi / 12) - 1;
  return {
    step: pitchClass.step,
    alter: pitchClass.alter,
    octave,
  };
};

const buildPitchXml = (midi: number) => {
  const pitch = midiToPitch(midi);
  return [
    '<pitch>',
    `<step>${pitch.step}</step>`,
    pitch.alter == null ? '' : `<alter>${pitch.alter}</alter>`,
    `<octave>${pitch.octave}</octave>`,
    '</pitch>',
  ].join('');
};

const buildRestXml = (staff: StaffNumber, duration: number, type: DurationType, measure = false) =>
  [
    '<note>',
    measure ? '<rest measure="yes"/>' : '<rest/>',
    `<duration>${duration}</duration>`,
    `<voice>${staff}</voice>`,
    `<type>${type}</type>`,
    `<staff>${staff}</staff>`,
    '</note>',
  ].join('');

const buildPedalDirectionXml = (pedalDown: boolean, offset: number) =>
  [
    '<direction placement="below">',
    '<direction-type>',
    `<pedal type="${pedalDown ? 'start' : 'stop'}"/>`,
    '</direction-type>',
    offset > 0 ? `<offset sound="yes">${offset}</offset>` : '',
    '<staff>1</staff>',
    '</direction>',
  ].join('');

const buildOrnamentXml = (ornament?: OrnamentType) => {
  if (ornament === 'trill') {
    return '<ornaments><trill-mark/></ornaments>';
  }
  return '';
};

const buildNotationsXml = (tieStop: boolean, tieStart: boolean, ornament?: OrnamentType) => {
  const contents = [
    tieStop ? '<tied type="stop"/>' : '',
    tieStart ? '<tied type="start"/>' : '',
    buildOrnamentXml(ornament),
  ]
    .filter(Boolean)
    .join('');

  return contents ? `<notations>${contents}</notations>` : '';
};

const buildChordXml = (
  midiNotes: number[],
  staff: StaffNumber,
  duration: number,
  type: DurationType,
  tieStop: boolean,
  tieStart: boolean,
  ornament?: OrnamentType,
) => {
  const sortedNotes = midiNotes
    .map((midi) => Math.max(0, Math.min(127, Math.round(midi))))
    .sort((a, b) => a - b);

  if (sortedNotes.length === 0) {
    return buildRestXml(staff, duration, type);
  }

  return sortedNotes
    .map((midi, index) =>
      [
        '<note>',
        index === 0 ? '' : '<chord/>',
        buildPitchXml(midi),
        `<duration>${duration}</duration>`,
        `<voice>${staff}</voice>`,
        `<type>${type}</type>`,
        tieStop ? '<tie type="stop"/>' : '',
        tieStart ? '<tie type="start"/>' : '',
        `<staff>${staff}</staff>`,
        buildNotationsXml(tieStop, tieStart, index === 0 ? ornament : undefined),
        '</note>',
      ].join(''),
    )
    .join('');
};

const splitStaffNotes = (midiNotes: number[]) => {
  const treble = midiNotes.filter((midi) => midi >= STAFF_SPLIT_MIDI);
  const bass = midiNotes.filter((midi) => midi < STAFF_SPLIT_MIDI);
  return { treble, bass };
};

const getSingleNoteStaff = (event: LiveComposerEvent): StaffNumber | null => {
  if (event.midiNotes.length !== 1) return null;
  return event.midiNotes[0] >= STAFF_SPLIT_MIDI ? 1 : 2;
};

const buildNotationEvents = (events: LiveComposerEvent[]): NotatedComposerEvent[] => {
  const notationEvents: NotatedComposerEvent[] = [];

  for (let index = 0; index < events.length; ) {
    const firstEvent = events[index];
    const firstStaff = getSingleNoteStaff(firstEvent);
    const firstNote = firstEvent.midiNotes[0];

    if (
      firstStaff == null ||
      firstEvent.durationDivisions !== TRILL_REQUIRED_DIVISIONS ||
      firstEvent.midiNotes.length !== 1
    ) {
      notationEvents.push({
        midiNotes: firstEvent.midiNotes,
        restBeforeDivisions: firstEvent.restBeforeDivisions,
        durationDivisions: firstEvent.durationDivisions,
      });
      index += 1;
      continue;
    }

    const secondEvent = events[index + 1];
    const secondStaff = secondEvent ? getSingleNoteStaff(secondEvent) : null;
    const secondNote = secondEvent?.midiNotes[0];

    if (
      !secondEvent ||
      secondStaff == null ||
      secondStaff !== firstStaff ||
      secondEvent.restBeforeDivisions !== 0 ||
      secondEvent.durationDivisions !== TRILL_REQUIRED_DIVISIONS ||
      secondNote == null ||
      secondNote === firstNote ||
      Math.abs(secondNote - firstNote) > TRILL_MAX_INTERVAL_SEMITONES
    ) {
      notationEvents.push({
        midiNotes: firstEvent.midiNotes,
        restBeforeDivisions: firstEvent.restBeforeDivisions,
        durationDivisions: firstEvent.durationDivisions,
      });
      index += 1;
      continue;
    }

    let runLength = 2;
    let lookaheadIndex = index + 2;

    while (lookaheadIndex < events.length) {
      const candidate = events[lookaheadIndex];
      const candidateStaff = getSingleNoteStaff(candidate);
      const expectedNote = runLength % 2 === 0 ? firstNote : secondNote;

      if (
        candidateStaff == null ||
        candidateStaff !== firstStaff ||
        candidate.restBeforeDivisions !== 0 ||
        candidate.durationDivisions !== TRILL_REQUIRED_DIVISIONS ||
        candidate.midiNotes[0] !== expectedNote
      ) {
        break;
      }

      runLength += 1;
      lookaheadIndex += 1;
    }

    if (runLength >= TRILL_MIN_NOTES) {
      notationEvents.push({
        midiNotes: [firstNote],
        restBeforeDivisions: firstEvent.restBeforeDivisions,
        durationDivisions: runLength * TRILL_REQUIRED_DIVISIONS,
        ornament: 'trill',
      });
      index += runLength;
      continue;
    }

    notationEvents.push({
      midiNotes: firstEvent.midiNotes,
      restBeforeDivisions: firstEvent.restBeforeDivisions,
      durationDivisions: firstEvent.durationDivisions,
    });
    index += 1;
  }

  return notationEvents;
};

const decomposeDuration = (duration: number) => {
  const parts: Array<{ duration: number; type: DurationType }> = [];
  let remaining = Math.max(0, Math.round(duration));

  for (const part of DURATION_PARTS) {
    while (remaining >= part.duration) {
      parts.push(part);
      remaining -= part.duration;
    }
  }

  return parts;
};

const appendStaffMaterial = ({
  notes,
  staff,
  startDivision,
  durationDivisions,
  ornament,
  measureXml,
}: {
  notes: number[];
  staff: StaffNumber;
  startDivision: number;
  durationDivisions: number;
  ornament?: OrnamentType;
  measureXml: Array<{ treble: string[]; bass: string[] }>;
}) => {
  let cursor = startDivision;
  let remaining = Math.max(1, Math.round(durationDivisions));
  const noteChunks: Array<{
    measureIndex: number;
    duration: number;
    type: DurationType;
  }> = [];

  while (remaining > 0) {
    const measureIndex = Math.floor(cursor / MEASURE_DURATION);
    const measureOffset = cursor % MEASURE_DURATION;
    const availableInMeasure = MEASURE_DURATION - measureOffset;
    const chunkDuration = Math.min(remaining, availableInMeasure);
    const parts = decomposeDuration(chunkDuration);

    parts.forEach((part) => {
      noteChunks.push({
        measureIndex,
        duration: part.duration,
        type: part.type,
      });
    });

    cursor += chunkDuration;
    remaining -= chunkDuration;
  }

  noteChunks.forEach((chunk, index) => {
    const xml = buildChordXml(
      notes,
      staff,
      chunk.duration,
      chunk.type,
      index > 0,
      index < noteChunks.length - 1,
      index === 0 ? ornament : undefined,
    );

    if (staff === 1) {
      measureXml[chunk.measureIndex].treble.push(xml);
    } else {
      measureXml[chunk.measureIndex].bass.push(xml);
    }
  });
};

const buildMeasureXml = (
  measureIndex: number,
  measure: { directions: string[]; treble: string[]; bass: string[] },
) => {
  const attributes =
    measureIndex === 0
      ? [
          '<attributes>',
          `<divisions>${DIVISIONS}</divisions>`,
          '<key><fifths>0</fifths></key>',
          '<time><beats>4</beats><beat-type>4</beat-type></time>',
          '<staves>2</staves>',
          '<clef number="1"><sign>G</sign><line>2</line></clef>',
          '<clef number="2"><sign>F</sign><line>4</line></clef>',
          '</attributes>',
        ].join('')
      : '';

  const trebleXml =
    measure.treble.length > 0
      ? measure.treble.join('')
      : buildRestXml(1, MEASURE_DURATION, 'whole', true);
  const bassXml =
    measure.bass.length > 0
      ? measure.bass.join('')
      : buildRestXml(2, MEASURE_DURATION, 'whole', true);

  return [
    `<measure number="${measureIndex + 1}">`,
    attributes,
    measure.directions.join(''),
    trebleXml,
    `<backup><duration>${MEASURE_DURATION}</duration></backup>`,
    bassXml,
    '</measure>',
  ].join('');
};

export const buildLiveComposerMusicXml = (
  events: LiveComposerEvent[],
  pedalEvents: LiveComposerPedalEvent[] = [],
): LiveComposerScoreBuild => {
  const notationEvents = buildNotationEvents(events);
  const totalDurationDivisions = events.reduce(
    (sum, event) =>
      sum +
      Math.max(0, Math.round(event.restBeforeDivisions)) +
      Math.max(1, Math.round(event.durationDivisions)),
    0,
  );
  const furthestPedalDivision = pedalEvents.reduce(
    (max, event) => Math.max(max, Math.max(0, Math.round(event.positionDivisions))),
    0,
  );
  const measureCount = Math.max(
    MIN_MEASURES,
    Math.ceil(
      (Math.max(totalDurationDivisions, furthestPedalDivision, 1) + EXTRA_DIVISIONS_AHEAD) /
        MEASURE_DURATION,
    ),
  );

  const measureXml = Array.from({ length: measureCount }, () => ({
    directions: [] as string[],
    treble: [] as string[],
    bass: [] as string[],
  }));

  let cursorDivision = 0;

  notationEvents.forEach((event) => {
    const restBeforeDivisions = Math.max(0, Math.round(event.restBeforeDivisions));
    const durationDivisions = Math.max(1, Math.round(event.durationDivisions));
    if (restBeforeDivisions > 0) {
      appendStaffMaterial({
        notes: [],
        staff: 1,
        startDivision: cursorDivision,
        durationDivisions: restBeforeDivisions,
        measureXml,
      });

      appendStaffMaterial({
        notes: [],
        staff: 2,
        startDivision: cursorDivision,
        durationDivisions: restBeforeDivisions,
        measureXml,
      });

      cursorDivision += restBeforeDivisions;
    }

    const { treble, bass } = splitStaffNotes(event.midiNotes);

    appendStaffMaterial({
      notes: treble,
      staff: 1,
      startDivision: cursorDivision,
      durationDivisions,
      ornament: event.ornament,
      measureXml,
    });

    appendStaffMaterial({
      notes: bass,
      staff: 2,
      startDivision: cursorDivision,
      durationDivisions,
      ornament: event.ornament,
      measureXml,
    });

    cursorDivision += durationDivisions;
  });

  pedalEvents
    .slice()
    .sort((a, b) => a.positionDivisions - b.positionDivisions || a.capturedAtMs - b.capturedAtMs)
    .forEach((event) => {
      const absoluteDivisions = Math.max(0, Math.round(event.positionDivisions));
      const measureIndex = Math.floor(absoluteDivisions / MEASURE_DURATION);
      if (!measureXml[measureIndex]) return;
      const offset = absoluteDivisions % MEASURE_DURATION;
      measureXml[measureIndex].directions.push(buildPedalDirectionXml(event.pedalDown, offset));
    });

  const measures = measureXml
    .map((measure, measureIndex) => buildMeasureXml(measureIndex, measure))
    .join('');

  const xml = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<!DOCTYPE score-partwise PUBLIC
  "-//Recordare//DTD MusicXML 3.1 Partwise//EN"
  "http://www.musicxml.org/dtds/partwise.dtd">
<score-partwise version="3.1">
  <part-list>
    <score-part id="P1">
      <part-name>Live Draft</part-name>
    </score-part>
  </part-list>
  <part id="P1">
    ${measures}
  </part>
</score-partwise>`;

  return {
    xml,
    measureCount,
  };
};
